import Keycloak from 'keycloak-js'
import useApplication from '@/hooks/useApplication'
import useBasket from '@/hooks/useBasket'
import useSession from '@/hooks/useSession'
import useI18n from '@/hooks/useI18n'
import { cloneDeep } from 'lodash'

let keycloak

const keycloakOptions = {
  url: import.meta.env.VITE_KEYCLOAK_ENDPOINT,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
}

async function initKeycloak(redirectUri, enforceLogin = true) {
  keycloak = new Keycloak(keycloakOptions)
  await keycloak.init({
    onLoad: enforceLogin ? 'login-required' : 'check-sso',
    useNonce: false,
    responseMode: 'query',
    redirectUri,
  })
}

async function getToken(platform) {
  switch (platform) {
    case 'ios':
      return new Promise((resolve, reject) => {
        return window.getAppAccessTokenCb(resolve, reject)
      })
    case 'android':
      return window.Auth?.getAppAccessToken()
    default:
      if (!keycloak) {
        await initKeycloak(window.location.href, false)
      }

      try {
        await keycloak.updateToken()
        return keycloak.token
      } catch {
        return undefined
      }
  }
}

async function isExistingCustomerLoggedIn() {
  if (!keycloak) {
    await initKeycloak(window.location.href, false)
  }
  return !!keycloak.token
}

async function loginExistingCustomer(redirectUri, locale) {
  if (!keycloak) {
    await initKeycloak(redirectUri)
  } else {
    await keycloak.login({ locale, redirectUri })
  }
}

/*
  Removes the basket from the session and the localstorage and reloads the page
  In case of an existing customer, the user is logged out as well (if previously logged in)
 */
async function restartApplication(redirectUri) {
  const { basket } = useBasket()
  const __basket = cloneDeep(basket)
  const { clearSession } = useSession()
  await clearSession()

  // TODO: Will this still work with Tab sync?
  if (!__basket.existingCustomer) {
    window.location.reload()
    return
  }

  const { i18n } = useI18n()
  const { getDvpUrl } = useApplication()
  const __redirectUri = redirectUri || getDvpUrl(i18n.global.locale.value)

  if (!keycloak) {
    try {
      await initKeycloak(__redirectUri, false)
    } catch {
      // On mobile platforms (ios/android) there's no keycloak so just reload the page
      window.location.reload()
      return
    }
  }

  const isLoggedIn = !!keycloak?.token

  if (isLoggedIn) {
    await keycloak.logout({ redirectUri: __redirectUri })
  } else {
    window.location.reload()
  }
}

export default function useAuth() {
  return {
    getToken,
    isExistingCustomerLoggedIn,
    loginExistingCustomer,
    restartApplication,
  }
}
